import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft, ShieldCheck, WifiOff, Puzzle, KeyRound, Users, Clock, Lock, FileText } from "lucide-react";

function Step({ n, title, children }) {
  return (
    <div className="flex gap-3" data-testid={`guide-step-${n}`}>
      <div className="flex-shrink-0 h-6 w-6 flex items-center justify-center bg-primary text-primary-foreground text-xs font-bold">{n}</div>
      <div className="min-w-0">
        <p className="text-sm font-semibold tracking-tight">{title}</p>
        <div className="text-xs text-muted-foreground mt-1 space-y-1 leading-relaxed">{children}</div>
      </div>
    </div>
  );
}

function Section({ icon: Icon, title, children, testid }) {
  return (
    <section className="border border-border bg-surface" data-testid={testid}>
      <div className="px-4 py-3 border-b border-border flex items-center gap-2">
        <Icon className="h-4 w-4 text-primary" />
        <h2 className="text-sm font-semibold tracking-tight">{title}</h2>
      </div>
      <div className="p-4 space-y-4">{children}</div>
    </section>
  );
}

export default function Guide() {
  return (
    <div className="min-h-screen w-full bg-background text-foreground">
      <header className="h-12 border-b border-border flex items-center px-4 gap-3">
        <Link to="/" data-testid="guide-back-link">
          <Button variant="ghost" size="sm" className="rounded-none h-8 text-xs text-muted-foreground"><ArrowLeft className="h-4 w-4 mr-1.5" /> Back to Scrubber</Button>
        </Link>
        <span className="text-sm font-semibold tracking-tight flex items-center gap-2 ml-2"><FileText className="h-4 w-4 text-primary" /> About &amp; Test Guide</span>
        <span className="text-[10px] uppercase tracking-[0.2em] text-primary border border-primary/40 px-2 py-1 ml-auto">SoverenX Scrubber</span>
      </header>

      <div className="max-w-3xl mx-auto p-6 md:p-8 space-y-6" data-testid="guide-content">
        <Section icon={ShieldCheck} title="What this tool is" testid="guide-about">
          <p className="text-xs text-muted-foreground leading-relaxed">
            <strong className="text-foreground">SoverenX Scrubber</strong> is a privacy-first tool that removes sensitive data from network/config files before you share them, and later lets you put the real values back. It is built for a share-with-contractor workflow: sanitize → hand over → they test → you restore → send to customer.
          </p>
          <div className="flex items-start gap-2 text-xs text-primary border border-primary/40 bg-primary/10 px-3 py-2">
            <WifiOff className="h-4 w-4 flex-shrink-0 mt-0.5" />
            <span>All reading, scrubbing and restoring happen <strong>inside your browser</strong>. Config files and the restore key are <strong>never uploaded</strong> to any server.</span>
          </div>
        </Section>

        <Section icon={FileText} title="What it does" testid="guide-features">
          <ul className="text-xs text-muted-foreground space-y-1.5 list-disc pl-4">
            <li><strong className="text-foreground">Detects &amp; scrubs</strong>: passwords/secrets/keys, usernames, public IPs (→ APIPA), emails, MAC addresses, and custom customer/location keywords.</li>
            <li><strong className="text-foreground">Uniform, reversible placeholders</strong>: same value → same token; public IPs stay valid-looking so the file is still testable.</li>
            <li><strong className="text-foreground">Batch mode</strong>: sanitize many files at once, download all as a .zip.</li>
            <li><strong className="text-foreground">Restore</strong>: reassemble the real values using a locally-stored key file (optionally passphrase-encrypted).</li>
            <li><strong className="text-foreground">Security</strong>: admin-managed accounts, mandatory 2FA, strong-password policy, 30-min inactivity sign-out, account lockout.</li>
          </ul>
        </Section>

        <Section icon={FileText} title="Step-by-step: functional test" testid="guide-functional">
          <Step n={1} title="Log in with 2FA">
            <p>Sign in with the email + temp password from your admin. First login forces a <strong>new password</strong> (8+ chars, 1 uppercase, 1 special, entered twice), then a <strong>QR code</strong> to register Google/Microsoft Authenticator. Confirm you then land on the scrubber.</p>
          </Step>
          <Step n={2} title="Sanitize a single file">
            <p>Drag in a <code>.cfg/.conf/.txt/.xml/.json</code> file containing passwords, a public IP (e.g. 8.8.8.8), a private IP (e.g. 192.168.1.1), an email and a MAC. Verify: secrets → <code>⟪SECRET_n⟫</code>, public IP → 169.254.x.x, <strong>private IP unchanged</strong>, email/MAC/username replaced. Check the change summary counts.</p>
          </Step>
          <Step n={3} title="Custom keywords &amp; toggles">
            <p>Add a keyword (e.g. a customer name) and confirm it gets replaced. Toggle a rule off / switch Replace↔Remove and confirm the output updates live.</p>
          </Step>
          <Step n={4} title="Batch + downloads">
            <p>Drop several files at once, switch between file tabs, then use <strong>Download All (.zip)</strong> and single-file download. Confirm files download and contents are sanitized.</p>
          </Step>
          <Step n={5} title="Download the restore key">
            <p>In the sidebar <strong>Restore Key</strong> panel, optionally set a passphrase, then <strong>Download Restore Key</strong> (<code>restore-key.sxmap</code>). Keep it private.</p>
          </Step>
          <Step n={6} title="Simulate the contractor & restore">
            <p>Open the sanitized file in a text editor, <strong>rename some config sections</strong> (but leave the placeholder tokens intact), save it. Go to <strong>Restore Secrets</strong>, drop the edited file + your key (enter passphrase if used), click <strong>Put the puzzle back</strong>. Verify original values return and the report shows tokens restored / any missing.</p>
          </Step>
          <Step n={7} title="Privacy check">
            <p>With browser DevTools → Network open, repeat sanitize &amp; restore. Confirm <strong>no request carries file contents or the key</strong> to the server (only login/user calls to <code>/api</code>).</p>
          </Step>
        </Section>

        <Section icon={Lock} title="Step-by-step: security &amp; access test" testid="guide-security">
          <Step n={1} title="Password policy">
            <p>Try setting a weak password (short / no uppercase / no special) at any password screen — each must be rejected with a clear message.</p>
          </Step>
          <Step n={2} title="Lockout">
            <p>Enter a wrong password repeatedly. After <strong>5 attempts</strong> the account locks for <strong>15 minutes</strong>; after <strong>3 such rounds</strong> it fully locks (admin must reset).</p>
          </Step>
          <Step n={3} title="Inactivity sign-out">
            <p>Leave a session idle: a warning appears at 5 min and 2 min before, and it <strong>auto signs out at 30 minutes</strong>.</p>
          </Step>
          <Step n={4} title="Admin: invites, reset, roles">
            <p>As admin: generate an <strong>invite link</strong>, open it in a private window, set a password + enroll 2FA. Test <strong>Reset PW</strong> and <strong>Reset 2FA</strong>. Confirm non-admins can't reach <code>/admin</code>.</p>
          </Step>
        </Section>

        <Section icon={Clock} title="Step-by-step: stress test" testid="guide-stress">
          <Step n={1} title="Large files">
            <p>Sanitize very large configs (e.g. 5–50 MB / tens of thousands of lines) and confirm the UI stays responsive and the diff/summary render.</p>
          </Step>
          <Step n={2} title="Many files at once">
            <p>Drop 20–100 files in batch mode; verify tabs, per-file counts, and the .zip download all complete.</p>
          </Step>
          <Step n={3} title="Token volume + restore">
            <p>Use a file with hundreds of unique secrets/IPs; download the key; restore and confirm all tokens map back and timing is acceptable.</p>
          </Step>
          <Step n={4} title="Concurrent users / auth load">
            <p>Have multiple testers log in simultaneously and hammer login (valid + invalid) to confirm 2FA, lockout and sessions behave under load.</p>
          </Step>
          <Step n={5} title="Edge inputs">
            <p>Try unsupported file types (should be rejected), empty files, files with no secrets, tokens duplicated/edited by hand, wrong key passphrase (should error cleanly).</p>
          </Step>
        </Section>

        <div className="flex flex-wrap gap-3 pt-1" data-testid="guide-cta">
          <Link to="/"><Button className="rounded-none bg-primary text-primary-foreground hover:bg-accent-hover"><ShieldCheck className="h-4 w-4 mr-2" /> Go Sanitize</Button></Link>
          <Link to="/restore"><Button variant="outline" className="rounded-none border-border"><Puzzle className="h-4 w-4 mr-2" /> Go Restore</Button></Link>
        </div>

        <p className="text-[10px] leading-relaxed text-muted-foreground/60 pt-2" data-testid="guide-disclaimer">
          © {new Date().getFullYear()} SoverenX Systems. This tool, its name and logo are the intellectual property of SoverenX Systems. All rights reserved.
        </p>
      </div>
    </div>
  );
}
